import { Day, Locale, addDays, getISODay, isSameDay, set, startOfDay } from "date-fns";
import { TFunction } from "i18next";
import { v4 } from "uuid";
import * as Yup from "yup";

import { AppointmentResponse, AttendanceType, DayOfWeek } from "@/api";
import {
  AllDayEndHour,
  AllDayStartHour,
  WEEKDAY_COUNT,
} from "@/common/components/appointments/shared/appointmentConstants";
import { nameof } from "@/common/helper/commonHelper";

import {
  AttendanceEntryFormValues,
  AttendanceEntryMaxLength,
  WeeklyPlanningDayFormValues,
} from "./PlanAttendanceDialog.types";

export const MAX_PRESENT_ENTRIES_PER_DAY = 2;
export const MAX_ABSENT_ENTRIES_PER_DAY = 1;

export const ISO_TO_DOW: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
];

export const WEEKDAYS = ISO_TO_DOW.slice(0, WEEKDAY_COUNT);

export function dateToDayOfWeek(date: Date): DayOfWeek {
  return ISO_TO_DOW[getISODay(date) - 1] ?? DayOfWeek.Monday;
}

export function formatWeekdayName(weekday: DayOfWeek, locale: Locale | undefined): string {
  const isoIndex = ISO_TO_DOW.indexOf(weekday);
  const jsDayIndex = ((isoIndex >= 0 ? isoIndex : 0) + 1) % 7;
  return locale?.localize.day(jsDayIndex as Day, { width: "wide" }) ?? String(jsDayIndex);
}

// Absences are all-day events; the backend stores them with no meaningful start/end time (00:00–00:00).
// For display and editing we surface the standard work-day hours instead of midnight.
export function buildAbsenceTimes(date: Date): { startTime: Date; endTime: Date } {
  return {
    startTime: set(date, { hours: AllDayStartHour, minutes: 0, seconds: 0, milliseconds: 0 }),
    endTime: set(date, { hours: AllDayEndHour, minutes: 0, seconds: 0, milliseconds: 0 }),
  };
}

export function createEmptyEntry(): AttendanceEntryFormValues {
  return {
    key: v4(),
    purposeKey: "",
    attendanceType: null,
    startTime: null,
    endTime: null,
    note: "",
    substituteCounselorId: null,
  };
}

export function buildInitialDays(weekStart: Date, existing: AppointmentResponse[]): WeeklyPlanningDayFormValues[] {
  return Array.from({ length: WEEKDAY_COUNT }, (_, i) => {
    const date = addDays(weekStart, i);
    const dayStart = startOfDay(date);

    const dayEntries = existing.filter((a) => isSameDay(a.startTimeUtc, dayStart));

    const entries: AttendanceEntryFormValues[] =
      dayEntries.length > 0
        ? dayEntries.map((a) => {
            const attendanceType = a.attendanceType ?? null;
            const isAbsent = attendanceType === AttendanceType.Absent;
            const times = isAbsent ? buildAbsenceTimes(date) : { startTime: a.startTimeUtc, endTime: a.endTimeUtc };
            return {
              key: a.appointmentId,
              purposeKey: a.purposeKey,
              attendanceType,
              startTime: times.startTime,
              endTime: times.endTime,
              note: a.details ?? "",
              substituteCounselorId: a.substituteCounselorId ?? null,
            };
          })
        : [createEmptyEntry()];

    return { date, entries };
  });
}

// ── Shared entry schema ────────────────────────────────────────────────────

export function attendanceEntrySchema(t: TFunction): Yup.ObjectSchema<AttendanceEntryFormValues> {
  return Yup.object({
    key: Yup.string().required(),
    purposeKey: Yup.string().when(
      [
        nameof<AttendanceEntryFormValues>("attendanceType"),
        nameof<AttendanceEntryFormValues>("startTime"),
        nameof<AttendanceEntryFormValues>("endTime"),
        nameof<AttendanceEntryFormValues>("note"),
        nameof<AttendanceEntryFormValues>("substituteCounselorId"),
      ],
      (values: unknown[], schema) => {
        const [attendanceType, startTime, endTime, note, substituteCounselorId] = values as [
          AttendanceType | null,
          Date | null,
          Date | null,
          string,
          string | null,
        ];
        const hasAnyData =
          attendanceType !== null || startTime !== null || endTime !== null || !!note || !!substituteCounselorId;
        return hasAnyData
          ? schema.required(t("common.validation.fieldRequired", { fieldName: t("calendar.planAttendance.purpose") }))
          : schema;
      }
    ),
    attendanceType: Yup.mixed<AttendanceType>().nullable() as Yup.Schema<AttendanceType | null>,
    startTime: Yup.date()
      .nullable()
      .test(
        "start-time-required",
        t("common.validation.fieldRequired", { fieldName: t("calendar.planAttendance.timeFrom") }),
        function (startTime) {
          const { purposeKey, attendanceType } = this.parent as Pick<
            AttendanceEntryFormValues,
            "purposeKey" | "attendanceType"
          >;
          if (!!purposeKey && attendanceType !== AttendanceType.Absent && !startTime) {
            return false;
          }
          return true;
        }
      ) as Yup.Schema<Date | null>,
    endTime: Yup.date()
      .nullable()
      .test(
        "end-time-required",
        t("common.validation.fieldRequired", { fieldName: t("calendar.planAttendance.timeTo") }),
        function (endTime) {
          const { purposeKey, attendanceType } = this.parent as Pick<
            AttendanceEntryFormValues,
            "purposeKey" | "attendanceType"
          >;
          if (!!purposeKey && attendanceType !== AttendanceType.Absent && !endTime) {
            return false;
          }
          return true;
        }
      )
      .test("end-after-start", t("calendar.planAttendance.endBeforeStart"), function (endTime) {
        const { startTime, attendanceType } = this.parent as Pick<
          AttendanceEntryFormValues,
          "startTime" | "attendanceType"
        >;
        // Absences are all-day events with no meaningful start/end time (stored as 00:00–00:00),
        // so the time-order check does not apply to them.
        if (attendanceType === AttendanceType.Absent || !startTime || !endTime) {
          return true;
        }
        const startMinutes = startTime.getHours() * 60 + startTime.getMinutes();
        const endMinutes = endTime.getHours() * 60 + endTime.getMinutes();
        return endMinutes > startMinutes;
      }) as Yup.Schema<Date | null>,
    note: Yup.string().max(AttendanceEntryMaxLength.note, ({ max }) =>
      t("common.validation.maxLength", { max })
    ) as Yup.Schema<string>,
    substituteCounselorId: Yup.string().nullable() as Yup.Schema<string | null>,
  });
}
